import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { Reveal } from "./Reveal";
import fullDiscography from "@/data/fullDiscography.json";

const STATUS_STYLES = {
  Released: { color: "#4FB04A", border: "rgba(79, 176, 74,0.4)" },
  Remix: { color: "#4f9bd5", border: "rgba(79,155,213,0.4)" },
  Unreleased: { color: "rgba(255,255,255,0.5)", border: "rgba(255,255,255,0.2)" },
  Shelved: { color: "#c85454", border: "rgba(200,84,84,0.4)" },
  Demo: { color: "#a78bfa", border: "rgba(167,139,250,0.4)" },
  "Co-Prod": { color: "#5ec9b0", border: "rgba(94,201,176,0.4)" },
};

const STATUS_FILTERS = ["All", "Released", "Remix", "Co-Prod", "Unreleased", "Shelved", "Demo"];
const DECADES = [
  { label: "All Years", from: 0, to: 9999 },
  { label: "1990s", from: 1990, to: 1999 },
  { label: "2000s", from: 2000, to: 2009 },
  { label: "2010s", from: 2010, to: 2019 },
  { label: "2020s", from: 2020, to: 2099 },
];

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Released;
  return (
    <span
      className="font-display uppercase text-[0.56rem] tracking-[0.16em] px-2 py-0.5 border rounded-full whitespace-nowrap"
      style={{ color: s.color, borderColor: s.border }}
    >
      {status}
    </span>
  );
}

export default function FullDiscography() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [decade, setDecade] = useState("All Years");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dec = DECADES.find((d) => d.label === decade) || DECADES[0];
    return fullDiscography.filter((g) => {
      if (filter !== "All" && !g.statuses.includes(filter)) return false;
      const yr = parseInt(g.year, 10);
      if (!isNaN(yr) && (yr < dec.from || yr > dec.to)) return false;
      if (dec.label !== "All Years" && isNaN(yr)) return false;
      if (!q) return true;
      const hay = `${g.year} ${g.project} ${g.artist} ${g.tracks.join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, filter, decade]);

  const totalTracks = useMemo(
    () => filtered.reduce((n, g) => n + g.tracks.length, 0),
    [filtered]
  );

  // If the user searches just a year, jump to that year's first entry
  useEffect(() => {
    const q = query.trim();
    if (/^\d{4}$/.test(q)) {
      const t = setTimeout(() => {
        const el = document.querySelector(`[data-year="${q}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [query, filtered]);

  return (
    <section id="full-discography" className="relative bg-obsidian py-24 md:py-36 border-t border-white/10" data-testid="full-discography-section">
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-4xl mb-14 md:mb-20">
          <p className="overline text-gold mb-6">The Complete Works · Every Credit, In Order</p>
          <h2 className="headline text-4xl md:text-6xl text-white mb-8">The Complete Timbaland Legacy Archive</h2>
          <p className="font-editorial italic text-2xl md:text-4xl text-white leading-snug mb-8">
            The internet didn't build this. The culture did.
          </p>
          <div className="mb-8 border-l-2 border-gold pl-5">
            <p className="font-body text-white/85 text-base md:text-lg leading-relaxed">
              This is the <span className="text-gold font-semibold">only place on the internet</span> where you can search
              Timbaland's <span className="text-gold font-semibold">entire</span> catalog, every credit hand-researched and
              verified, not scraped from a wiki and not generated by AI.
            </p>
          </div>
          <div className="space-y-5 font-body text-white/60 text-base md:text-lg leading-relaxed">
            <p>
              This Timbaland discography has been researched and collected manually since 1998, track by track,
              credit by credit, with contributions from fans who understood that this legacy was too important to
              leave undocumented.
            </p>
            <p>
              This isn't a list copied from Wikipedia, and isn't something generated overnight using AI. This is
              nearly 30 years of research, corrections, rare finds, forgotten credits, and music history preserved
              in one place.
            </p>
            <p>
              A wiki page can tell you Timbaland made hits, but this archive shows you just how much of the sound of
              modern music has his <span className="text-gold">fingerprints all over it.</span>
            </p>
          </div>
        </Reveal>

        {/* Controls */}
        <div className="sticky top-[76px] z-20 -mx-6 md:-mx-12 px-6 md:px-12 py-4 bg-obsidian/90 backdrop-blur-xl border-y border-white/10 mb-10">
          <p className="font-body text-sm text-white/50 mb-3" data-testid="disco-instructions">
            Type a song, album, artist, or a year (like <span className="text-gold">2007</span>) in the search box to jump straight to it, or tap the filters below to browse by status and decade.
          </p>
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a song, album, artist, or year (e.g. 2007)…"
                data-testid="disco-search"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 font-body text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold/50 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`disco-filter-${f.toLowerCase()}`}
                  className={`font-display uppercase text-[0.62rem] tracking-[0.14em] px-4 py-2 rounded-full border transition-all duration-300 ${
                    filter === f ? "bg-gold text-black border-gold" : "text-white/55 border-white/12 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="font-display uppercase tracking-[0.16em] text-[0.6rem] text-white/35 mr-1">Years:</span>
            {DECADES.map((d) => (
              <button
                key={d.label}
                onClick={() => setDecade(d.label)}
                data-testid={`disco-decade-${d.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-display uppercase text-[0.62rem] tracking-[0.14em] px-4 py-1.5 rounded-full border transition-all duration-300 ${
                  decade === d.label ? "bg-white text-black border-white" : "text-white/55 border-white/12 hover:border-white/40 hover:text-white"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <p className="font-body text-xs text-white/40 mt-3" data-testid="disco-count">
            {filtered.length} projects · {totalTracks} tracks
          </p>
        </div>

        {/* List */}
        <div className="divide-y divide-white/[0.07]">
          {filtered.map((g, i) => (
            <motion.div
              key={`${g.project}-${g.artist}-${g.year}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`full-disco-item-${i}`}
              data-year={g.year || ""}
              className="grid md:grid-cols-[92px_1fr] gap-3 md:gap-8 py-7 group"
            >
              <div className="shrink-0 flex md:flex-col items-center md:items-start gap-3">
                {g.art ? (
                  <img src={g.art} alt={g.project} loading="lazy" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-md border border-white/10" />
                ) : (
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center">
                    <span className="font-display text-gold/40 text-xl">T</span>
                  </div>
                )}
                <span className="font-display text-lg md:text-xl text-white/25 group-hover:text-gold transition-colors duration-500">
                  {g.year || "·"}
                </span>
              </div>
              <div>
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 mb-2">
                  <h3 className="font-editorial italic text-2xl md:text-3xl text-white leading-tight">{g.project}</h3>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {g.statuses.map((s) => (<StatusBadge key={s} status={s} />))}
                  </div>
                </div>
                <p className="font-display uppercase tracking-[0.16em] text-[0.7rem] text-gold mb-4">{g.artist}</p>
                <div className="flex flex-wrap gap-x-2 gap-y-2">
                  {g.tracks.map((t, ti) => (
                    <span
                      key={ti}
                      className="font-body text-sm text-white/55 bg-white/[0.04] border border-white/[0.06] rounded-md px-3 py-1.5 hover:text-white hover:border-white/20 transition-colors"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}

          {filtered.length === 0 && (
            <p className="font-body text-white/40 py-16 text-center" data-testid="disco-empty">
              No records match "{query}".
            </p>
          )}
        </div>

        <p className="mt-14 pt-8 border-t border-white/10 font-body text-sm text-white/40 max-w-3xl leading-relaxed" data-testid="catalog-credit">
          Researched and compiled manually by the Timbaland Archive since 1998. Every entry is verified track by
          track and credit by credit against label liner notes, official releases, and decades of fan documentation.
          This is not an AI-generated or copied list. Spot a missing credit? Corrections and additions are always welcome.
        </p>
      </div>
    </section>
  );
}
